import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export default function FloaterPage() {
  const { member } = useAuth();
  const [chart, setChart] = useState([]);
  const [sundaySettings, setSundaySettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showSundays, setShowSundays] = useState(false);

  const loadData = useCallback((isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    return Promise.all([
      api.get('/floaters/chart?year=2026'),
      api.get('/floaters/sunday-settings?year=2026'),
    ]).then(([c, ss]) => {
      setChart(c.data.chart || []);
      setSundaySettings(ss.data.settings || []);
      setLastUpdated(new Date());
    }).finally(() => {
      setLoading(false);
      setRefreshing(false);
    });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const tier1 = chart.filter(m => m.tier1);
  const tier2 = chart.filter(m => !m.tier1);

  const myEntry = chart.find(m => m.id === member?.id);
  const myTier2Rank = myEntry && !myEntry.tier1
    ? (() => {
        const i = tier2.findIndex(m => m.id === member?.id);
        if (i < 0) return null;
        return i === 0 ? 1 : (tier2[i].elapsed_weeks === tier2[i-1].elapsed_weeks
          ? tier2.slice(0,i).findIndex(x => x.elapsed_weeks === tier2[i].elapsed_weeks) + 1
          : i + 1);
      })()
    : null;

  const today = new Date();
  const pastSundays = sundaySettings
    .filter(s => new Date(s.match_date) <= today)
    .sort((a, b) => new Date(b.match_date) - new Date(a.match_date));

  // Member's last floater date — sundays ON OR BEFORE this date are N/A (not relevant)
  const myLastFloatDate = myEntry?.last_floated_date ? new Date(myEntry.last_floated_date) : null;
  // Tier 1 (never floated): all sundays are N/A — nothing counts yet
  const allSundaysNA = !myEntry || myEntry.tier1;

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* Header */}
      <div style={{ background: 'var(--navy)', padding: '16px 16px 14px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: '4rem', color: 'white', opacity: 0.04, pointerEvents: 'none', userSelect: 'none' }}>UCC</div>
        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.5)', marginBottom: 4, textTransform: 'uppercase' }}>SEASON 2026</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.6rem', fontWeight: 700, color: 'white', lineHeight: 1.1 }}>🔄 Floater Chart</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>View only · Updated by Admin after every match</div>
          </div>
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            style={{ marginTop: 4, padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '0.75rem', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
            {refreshing ? '⏳' : '🔄'} {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>

        {/* My Status Card */}
        {myEntry && (
          <div style={{
            background: myEntry.tier1 ? 'linear-gradient(135deg, #78350f, #b45309)' : 'linear-gradient(135deg, var(--navy), #1e3a6e)',
            borderRadius: 14, padding: '14px 16px', marginBottom: 14, color: 'white',
          }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, opacity: 0.7, marginBottom: 6, letterSpacing: '0.1em' }}>MY FLOATER STATUS</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: '1.05rem' }}>
                  {myEntry.tier1 ? '🥇 Tier 1 — Never Floated' : `🥈 Tier 2 · ${myEntry.elapsed_weeks} weeks elapsed`}
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: 3 }}>
                  {myEntry.tier1 ? 'Absolute first priority this season'
                    : `Last floated: ${myEntry.last_floated_date ? new Date(myEntry.last_floated_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}`}
                </div>
                {!myEntry.tier1 && myEntry.exclusions_count > 0 && (
                  <div style={{ fontSize: '0.68rem', opacity: 0.6, marginTop: 2 }}>
                    {myEntry.exclusions_count} week{myEntry.exclusions_count > 1 ? 's' : ''} not counted (batted up naturally)
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: '2rem', lineHeight: 1 }}>
                  {myEntry.tier1 ? `#${tier1.findIndex(m => m.id === member?.id) + 1}` : `#${myTier2Rank}`}
                </div>
                <div style={{ fontSize: '0.62rem', opacity: 0.65 }}>{myEntry.tier1 ? 'in Tier 1' : 'in Tier 2'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Rules Banner */}
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.75rem', color: '#0c4a6e', lineHeight: 1.7 }}>
          <strong>How floater priority works:</strong><br />
          🥇 <strong>Tier 1</strong> — Never been floater this season → goes first, always<br />
          🥈 <strong>Tier 2</strong> — Most weeks elapsed since last floater → goes next<br />
          ⚡ Weeks elapsed = Sundays since last floater <em>minus</em> weeks you batted up naturally<br />
          🚫 Only for UCC Rules matches. Not Series / ICC Sundays / Unofficial
        </div>

        {/* Sunday Counting — collapsible */}
        {pastSundays.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <button
              onClick={() => setShowSundays(v => !v)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: showSundays ? '10px 10px 0 0' : 10, border: '1px solid #e5e7eb', borderBottom: showSundays ? '1px solid #f3f4f6' : '1px solid #e5e7eb', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '0.82rem', color: 'var(--navy)' }}>
              <span>📅 Which Sundays counted? {allSundaysNA ? '(N/A — not yet floated)' : `(${pastSundays.filter(s => new Date(s.match_date) > myLastFloatDate && s.floater_counted && !s.exclusions?.includes(member?.id)).length} of ${pastSundays.filter(s => new Date(s.match_date) > myLastFloatDate).length} after your last floater)`}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{showSundays ? '▲ Hide' : '▼ Show'}</span>
            </button>

            {showSundays && (
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                {pastSundays.map((s, i) => {
                  const d = new Date(s.match_date);
                  const isSeries = s.match_type === 'Series' || !!s.series_id;
                  const isICC = s.match_type === 'ICC';
                  const myExclusion = Array.isArray(s.exclusions) && s.exclusions.includes(member?.id);
                  // N/A: member hasn't floated yet, OR this sunday is on/before their last floater date
                  const isNA = allSundaysNA || (myLastFloatDate && d <= myLastFloatDate);
                  const notCountedReason = !isNA && !s.floater_counted
                    ? (isICC ? 'ICC Sunday (last of month)' : isSeries ? 'Series Sunday' : 'Not counted')
                    : null;
                  return (
                    <div key={s.match_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: i < pastSundays.length - 1 ? '1px solid #f3f4f6' : 'none', background: isNA ? '#fafafa' : (!s.floater_counted || myExclusion) ? '#fffbf5' : 'white', opacity: isNA ? 0.55 : 1 }}>
                      <div style={{ flexShrink: 0, minWidth: 40, textAlign: 'center' }}>
                        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: '0.95rem', color: 'var(--navy)', lineHeight: 1 }}>{d.getDate()}</div>
                        <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', fontWeight: 700 }}>{d.toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: isNA ? '#9ca3af' : 'var(--navy)' }}>
                          {d.toLocaleDateString('en-IN', { weekday: 'short' })} · {s.match_type || 'Match'}
                        </div>
                        {!isNA && myExclusion && s.floater_counted && (
                          <div style={{ fontSize: '0.68rem', color: '#92400e' }}>⚠️ Not counted for you — batted up naturally</div>
                        )}
                        {notCountedReason && (
                          <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>{notCountedReason}</div>
                        )}
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        {isNA
                          ? <span style={{ fontSize: '0.65rem', background: '#f3f4f6', color: '#9ca3af', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>— N/A</span>
                          : !s.floater_counted
                            ? <span style={{ fontSize: '0.65rem', background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>⛔ Not Counted</span>
                            : myExclusion
                              ? <span style={{ fontSize: '0.65rem', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>⚠️ Not for you</span>
                              : <span style={{ fontSize: '0.65rem', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>✅ Counted</span>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tier 1 */}
        {tier1.length > 0 && (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', borderRadius: 20, padding: '4px 12px', marginBottom: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: '0.78rem' }}>
              🥇 TIER 1 — NEVER FLOATED THIS SEASON ({tier1.length})
            </div>
            <div style={{ background: 'white', border: '2px solid #fbbf24', borderRadius: 14, marginBottom: 14, overflow: 'hidden' }}>
              {tier1.map((m, i) => {
                const isMe = m.id === member?.id;
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: i < tier1.length - 1 ? '1px solid #fef3c7' : 'none', background: isMe ? '#fffbeb' : i === 0 ? '#fffde7' : 'white' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: i === 0 ? '#f59e0b' : '#fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: '0.8rem', color: '#78350f', flexShrink: 0 }}>#{i + 1}</div>
                    {m.photo_url
                      ? <img src={m.photo_url} alt={m.display_name} style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: '0.9rem', flexShrink: 0 }}>{m.display_name?.charAt(0)}</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: '0.95rem', color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {m.display_name}
                        {isMe && <span style={{ fontSize: '0.6rem', background: 'var(--navy)', color: 'white', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>YOU</span>}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{m.ucc_id} · #{m.jersey_no || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.65rem', background: i === 0 ? '#fef3c7' : '#f9fafb', color: i === 0 ? '#92400e' : '#6b7280', border: `1px solid ${i === 0 ? '#fbbf24' : '#e5e7eb'}`, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                        {i === 0 ? 'FIRST UP' : 'TIER 1'}
                      </span>
                      <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 3 }}>0× floated</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Tier 2 */}
        {tier2.length > 0 && (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 20, padding: '4px 12px', marginBottom: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: '0.78rem' }}>
              🥈 TIER 2 — BY ELAPSED WEEKS ({tier2.length})
            </div>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, marginBottom: 14, overflow: 'hidden' }}>
              {tier2.map((m, i) => {
                const isMe = m.id === member?.id;
                // Dense rank: count scores strictly higher than mine, +1
                const rank = 1 + new Set(tier2.map(x => x.elapsed_weeks).filter(w => w > m.elapsed_weeks)).size;
                const isTied = tier2.filter(x => x.elapsed_weeks === m.elapsed_weeks).length > 1;
                const isTop = rank <= 2;
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: i < tier2.length - 1 ? '1px solid #f3f4f6' : 'none', background: isMe ? '#eff6ff' : isTop ? '#f0fdf4' : 'white' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: isTop ? '#16a34a' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: '0.8rem', color: isTop ? 'white' : '#6b7280', flexShrink: 0 }}>#{rank}</div>
                    {m.photo_url
                      ? <img src={m.photo_url} alt={m.display_name} style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: '0.9rem', flexShrink: 0 }}>{m.display_name?.charAt(0)}</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: '0.95rem', color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {m.display_name}
                        {isMe && <span style={{ fontSize: '0.6rem', background: 'var(--navy)', color: 'white', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>YOU</span>}
                        {isTied && <span style={{ fontSize: '0.58rem', background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>TIE</span>}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                        Last: {m.last_floated_date ? new Date(m.last_floated_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                        {m.exclusions_count > 0 && ` · -${m.exclusions_count} wks excluded`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: '1.3rem', lineHeight: 1, color: isTop ? '#16a34a' : 'var(--navy)' }}>{m.elapsed_weeks}</div>
                      <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>wks elapsed</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {chart.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🔄</div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1rem' }}>No floater data yet</div>
            <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Chart will appear after the first UCC Rules match</div>
          </div>
        )}

        {lastUpdated && (
          <div style={{ textAlign: 'center', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Last refreshed {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        <div style={{ marginTop: 8, padding: '10px 14px', background: '#f8fafc', borderRadius: 10, fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
          💡 TIE = same elapsed weeks → captain decides on match day · Fine for floater rule violation: Rs.3,000 · Season resets 1st January
        </div>
      </div>
    </div>
  );
}
